/**
 * Certificate-only print helper.
 *
 * Fixes the "prints 4-5 pages of the whole app" bug: instead of
 * calling window.print() on the current page (which prints the
 * modal PLUS everything underneath), we open a fresh, hidden iframe
 * containing ONLY the certificate HTML + a QR code corner, then
 * print that iframe. The main page is never included.
 *
 * Public API:
 *   printCertificate({ innerHtml, title, url })
 *     - innerHtml : trusted HTML string for the certificate body
 *                   (caller renders it via document.querySelector +
 *                    .outerHTML on the certificate ref).
 *     - title     : optional document.title override.
 *     - url       : URL encoded into the QR corner (defaults to
 *                   https://myworld.quest).
 *
 * The iframe is removed as soon as the print dialog closes so we
 * don't leave a stale sandbox in the DOM.
 */

// tiny QR helper — uses qrcode.react's serialized SVG. We embed the
// SVG inline so the print window doesn't need to load anything.
import ReactDOMServer from "react-dom/server";
import React from "react";
import { QRCodeSVG } from "qrcode.react";

export const SITE_URL = "https://www.myworld.quest";

/** Serialize a QR into an inline SVG string. */
export function qrSvg(url = SITE_URL, size = 96) {
  return ReactDOMServer.renderToStaticMarkup(
    React.createElement(QRCodeSVG, {
      value: url,
      size,
      bgColor: "#FFFFFF",
      fgColor: "#0F172A",
      level: "M",
      includeMargin: false,
    })
  );
}

/**
 * Print a certificate isolated from the rest of the app.
 *
 * @param {Object} opts
 * @param {string} opts.innerHtml  - certificate HTML (usually certRef.current.outerHTML)
 * @param {string} [opts.title]    - browser document title while printing
 * @param {string} [opts.url]      - URL for the QR
 * @param {string} [opts.footerText] - small line under QR (e.g. "Scan to visit My World Quest")
 * @returns {Promise<void>} resolves after the print dialog closes.
 */
export function printCertificate({
  innerHtml,
  title = "My World Quest Certificate",
  url = SITE_URL,
  footerText = "Scan to visit My World Quest",
} = {}) {
  return new Promise((resolve) => {
    if (!innerHtml) { resolve(); return; }
    // Build a fresh iframe off-screen. Position offscreen so the
    // parent layout doesn't shift.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch { /* already gone */ }
      resolve();
    };

    const qr = qrSvg(url, 96);

    // Compose the doc. Inline critical Tailwind-ish styles so the
    // certificate looks the same in print regardless of the host
    // page's stylesheet. We deliberately do NOT inject the full app
    // CSS — the certificate stands alone.
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: auto; margin: 12mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: #0F172A;
      background: #ffffff;
    }
    .cert-wrap {
      max-width: 720px;
      margin: 0 auto;
      position: relative;
    }
    /* Neutralise app-only utility classes that hurt print. */
    .cert-wrap * { box-shadow: none !important; }
    .cert-wrap .print\\:hidden,
    .cert-wrap [data-print-hidden="true"] { display: none !important; }
    /* Force-show elements the app hid with print:hidden. */
    .cert-wrap [data-print-show="true"] { display: block !important; }
    /* QR corner */
    .cert-qr {
      position: absolute;
      right: 12px;
      bottom: 12px;
      background: #ffffff;
      border: 2px solid #0F172A;
      border-radius: 12px;
      padding: 6px;
      text-align: center;
      font-family: inherit;
      font-size: 9px;
      font-weight: 800;
      color: #64748b;
      width: 108px;
    }
    .cert-qr svg { display: block; margin: 0 auto; }
    .cert-qr .qr-caption { margin-top: 4px; line-height: 1.15; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="cert-wrap">
    ${innerHtml}
    <div class="cert-qr" aria-hidden="true">
      ${qr}
      <div class="qr-caption">${escapeHtml(footerText)}</div>
    </div>
  </div>
  <script>
    (function () {
      function done() {
        try { window.parent.postMessage({type: 'cert-print-done'}, '*'); } catch (e) {}
      }
      // Give layout a beat so images/SVG render before triggering print.
      setTimeout(function () {
        try { window.focus(); window.print(); }
        catch (e) { done(); return; }
        // afterprint fires on most browsers; fall back to a timer for older.
        window.onafterprint = done;
        setTimeout(done, 2000);
      }, 120);
    })();
  </script>
</body>
</html>`;

    // Listen for the child's "I'm done" ping so we can cleanup once.
    let cleaned = false;
    const onMessage = (ev) => {
      if (ev.data && ev.data.type === "cert-print-done" && !cleaned) {
        cleaned = true;
        window.removeEventListener("message", onMessage);
        cleanup();
      }
    };
    window.addEventListener("message", onMessage);

    // Hard timeout — never leak the iframe if postMessage never fires.
    setTimeout(() => {
      if (!cleaned) {
        cleaned = true;
        window.removeEventListener("message", onMessage);
        cleanup();
      }
    }, 4000);

    // Write the doc. Using srcdoc keeps the iframe same-origin so
    // window.print() can run without cross-frame restrictions.
    iframe.srcdoc = html;
  });
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
